'use client'

import React, { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import MarkdownErrorBoundary from './MarkdownErrorBoundary'
import { ChartRenderer } from './ChartRenderer'
import { TableRenderer } from './TableRenderer'
import type { ChartConfiguration } from 'chart.js'

/**
 * Safely parse JSON with fallback for common quote escaping issues
 * Only attempts sanitization if the initial JSON.parse fails
 */
function safeParseJson(jsonString: string): any {
  try {
    // First try normal JSON.parse - this handles all valid JSON
    return JSON.parse(jsonString)
  } catch (initialError) {
    try {
      // If that fails, try to fix common quote issues
      const sanitized = jsonString
        // Fix unescaped double quotes in string values
        .replace(/(?<=:\s*"[^"]*)"([^"]*)"([^"]*")/g, '$1\\"$2$3')
        // Fix unescaped quotes at the start of string values
        .replace(/(?<=:\s*)"([^"]*)"([^"]*")/g, '"$1\\"$2')
        // Fix unescaped quotes at the end of string values
        .replace(/([^"]*)"([^"]*"(?=\s*[,}]))/g, '$1\\"$2')

      return JSON.parse(sanitized)
    } catch (sanitizationError) {
      // If sanitization also fails, throw the original error
      throw initialError
    }
  }
}

interface ChatMarkdownProps {
  children: React.ReactNode
  enableYouTubeEmbed?: boolean
}

function extractYouTubeIds(text: string) {
  const ids = new Set<string>()
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{11})/g,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]{11})/g,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{11})/g,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/g,
  ]

  for (const pattern of patterns) {
    let match
    while ((match = pattern.exec(text)) !== null) {
      ids.add(match[1])
    }
  }

  return Array.from(ids)
}

export default function ChatMarkdown({
  children,
  enableYouTubeEmbed = false,
}: ChatMarkdownProps) {
  const raw = String(children)

  // Extract table configurations from ```table blocks
  const tableConfigs = useMemo(() => {
    const tables: Array<{ data: any; index: number }> = []
    const tableRegex = /```table\s*\n([\s\S]*?)\n\s*```/g
    let match

    while ((match = tableRegex.exec(raw)) !== null) {
      try {
        const data = safeParseJson(match[1])
        // Validate required fields
        if (
          data &&
          data.headers &&
          Array.isArray(data.headers) &&
          data.rows &&
          Array.isArray(data.rows) &&
          data.csvUrl
        ) {
          tables.push({ data, index: match.index })
        }
      } catch (error) {
        console.error('Failed to parse table config:', error)
      }
    }

    return tables
  }, [raw])

  // Extract chart configurations from multiple possible formats
  const chartConfigs = useMemo(() => {
    const charts: Array<{ config: ChartConfiguration; index: number }> = []

    // Case 1: Properly fenced ```chart blocks
    const fencedRegex = /```chart\s*\n([\s\S]*?)\n\s*```/g
    let match
    while ((match = fencedRegex.exec(raw)) !== null) {
      try {
        const config = safeParseJson(match[1])
        charts.push({ config, index: match.index })
      } catch (error) {
        console.error('Failed to parse fenced chart config:', error)
      }
    }

    // Case 2: Bare Chart.js JSON (not wrapped in fences)
    // Only look for this if we haven't found fenced charts
    if (charts.length === 0) {
      // Find all potential JSON start positions
      const jsonStarts: number[] = []
      const typeRegex = /"type"\s*:\s*"(bar|line|pie|doughnut|radar|scatter)"/gi
      while ((match = typeRegex.exec(raw)) !== null) {
        // Look backwards to find the opening brace
        let openBraceIndex = -1
        for (let i = match.index; i >= 0; i--) {
          if (raw[i] === '{') {
            openBraceIndex = i
            break
          }
        }
        if (openBraceIndex !== -1) {
          jsonStarts.push(openBraceIndex)
        }
      }

      // For each potential start, try to extract complete JSON
      for (const startIndex of jsonStarts) {
        try {
          // Find matching closing brace
          let braceCount = 0
          let endIndex = -1
          for (let i = startIndex; i < raw.length; i++) {
            if (raw[i] === '{') braceCount++
            if (raw[i] === '}') {
              braceCount--
              if (braceCount === 0) {
                endIndex = i + 1
                break
              }
            }
          }

          if (endIndex !== -1) {
            const jsonStr = raw.substring(startIndex, endIndex)
            // Validate it's actually chart JSON by checking for required fields
            if (jsonStr.includes('"type"') && jsonStr.includes('"data"')) {
              const config = safeParseJson(jsonStr)
              // Verify it has the required structure
              if (config && config.type && config.data) {
                charts.push({ config, index: startIndex })
              }
            }
          }
        } catch (error) {
          // Silently skip invalid/incomplete JSON
          console.warn('Skipping incomplete chart JSON at position', startIndex)
        }
      }
    }

    return charts
  }, [raw])

  // Remove chart and table blocks from content for markdown rendering
  const contentWithoutChartsAndTables = useMemo(() => {
    if (chartConfigs.length === 0 && tableConfigs.length === 0) return raw

    let cleaned = raw

    // Remove fenced chart blocks
    cleaned = cleaned.replace(/```chart\s*\n[\s\S]*?\n\s*```/g, '')

    // Remove fenced table blocks
    cleaned = cleaned.replace(/```table\s*\n[\s\S]*?\n\s*```/g, '')

    // Remove successfully parsed chart JSONs
    // Sort by index descending to remove from end first (avoids index shifting)
    const sortedCharts = [...chartConfigs].sort((a, b) => b.index - a.index)
    for (const chart of sortedCharts) {
      // Find the end of this JSON in the raw string
      const jsonStart = chart.index
      let braceCount = 0
      let jsonEnd = -1
      for (let i = jsonStart; i < raw.length; i++) {
        if (raw[i] === '{') braceCount++
        if (raw[i] === '}') {
          braceCount--
          if (braceCount === 0) {
            jsonEnd = i + 1
            break
          }
        }
      }
      if (jsonEnd !== -1) {
        cleaned = cleaned.substring(0, jsonStart) + cleaned.substring(jsonEnd)
      }
    }

    return cleaned.trim()
  }, [raw, chartConfigs, tableConfigs])

  // Heuristic: if content contains multiple /api/ paths OR table pipes, avoid GFM (which
  // triggers the buggy code path under React 19 sometimes). We still allow basic markdown.
  const disableGfm = useMemo(() => {
    const apiPathHits = (raw.match(/\b\/api\//g) || []).length
    const tablePipeHits = (raw.match(/\n[^\n]*\|[^\n]*\|/g) || []).length
    // Fenced code with language can also trigger plugin transforms
    const fencedBlocks = (raw.match(/```[a-zA-Z0-9_-]*\n[\s\S]*?```/g) || [])
      .length
    return apiPathHits >= 3 || tablePipeHits >= 2 || fencedBlocks >= 4
  }, [raw])

  // Provide stable plugin array; cast to any to avoid cross-instance type mismatches
  const remarkPlugins = useMemo(() => {
    if (disableGfm) return [] as any
    return [remarkGfm] as any
  }, [disableGfm])

  const youTubeIds = useMemo(() => {
    if (!enableYouTubeEmbed) return []
    return extractYouTubeIds(raw)
  }, [enableYouTubeEmbed, raw])

  return (
    <MarkdownErrorBoundary raw={raw}>
      <div className="chat-markdown prose prose-sm max-w-none">
        {/* Render markdown content (without chart/table blocks) */}
        {contentWithoutChartsAndTables && (
          <ReactMarkdown remarkPlugins={remarkPlugins}>
            {contentWithoutChartsAndTables}
          </ReactMarkdown>
        )}

        {/* Render charts */}
        {chartConfigs.map((chart, idx) => (
          <ChartRenderer key={idx} config={chart.config} />
        ))}

        {/* Render tables */}
        {tableConfigs.map((table, idx) => (
          <TableRenderer key={`table-${idx}`} data={table.data} />
        ))}

        {youTubeIds.map(id => (
          <div
            key={`youtube-${id}`}
            className="mt-3 overflow-hidden rounded-md border border-black/10 bg-black/5"
          >
            <iframe
              className="aspect-video w-full"
              src={`https://www.youtube.com/embed/${id}`}
              title="YouTube video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ))}

        {disableGfm && (
          <div className="mt-2 text-[10px] opacity-50">
            (Rendered without GFM to prevent crash)
          </div>
        )}
      </div>
    </MarkdownErrorBoundary>
  )
}
